<template>
  <div :key="`menu-${uid}`" class="target-menu" :id="`target-menu-${uid}`">
    <item-header type="target" :choosable="true" :selected-id="ctx.target.id" @select="selectTarget">
      <template #content>
        <editable-item-title :item="ctx.target" />
      </template>
    </item-header>
    <drawer
      :title="$t('labels.general')"
      :open="drawerStates.targetGeneral"
      @update:open="drawerStates.targetGeneral = $event"
    >
      <div class="column gap-4">
        <input-text
          ref="titleField"
          :placeholder="$t('labels.title')"
          :prevent-submit="true"
          :label="$t('labels.title')"
          :value="ctx.target.title"
          @enter="descriptionField?.focus()"
          @update:value="ctx.updateKey('title', $event)"
        />
        <input-text-editor
          ref="descriptionField"
          :label="$t('labels.description')"
          :value="ctx.target.description"
          @update:value="ctx.updateKey('description', $event)"
        />
      </div>
    </drawer>

    <drawer
      v-if="features.indicator"
      :title="$t('objectNames.indicator', 2)"
      v-model:open="drawerStates.targetIndicators"
    >
      <indicator-list :target-id="ctx.target.id" />
    </drawer>

    <drawer
      :title="$t('labels.status')"
      v-model:open="drawerStates.targetStatus"
    >
      <value-menu :target-id="ctx.target.id" />
    </drawer>

    <!-- IMPACTS -->
    <drawer
      v-if="features.impact"
      :title="$t('objectNames.impact', 2)"
      v-model:open="drawerStates.targetImpacts"
    >
      <div class="column gap-6 mt-1">
        <relation-list
          type="outgoing"
          :element-uid="ctx.target.uid"
          :selected-relation-id="selectedRelation?.id"
          @select="selectSub"
        />

        <relation-list
          type="incoming"
          :element-uid="ctx.target.uid"
          :selected-relation-id="selectedRelation?.id"
          @select="selectSub"
        />
      </div>
    </drawer>

    <drawer
      v-if="features.flow"
      v-model:open="drawerStates.targetFlows"
      :title="$t('objectNames.flow', 5)"
    >
      <flow-list
        :element-type="ctx.target.type"
        :element-id="ctx.target.id"
        :sub-selection="subSelection"
        @select="emit('select', $event)"
      />
    </drawer>

    <!-- MOMENTUM -->
    <drawer
      v-if="features.momentum"
      :title="$t('objectNames.momentum')"
      v-model:open="drawerStates.targetMomentum"
    >
      <div class="line-docanchor">
        <label class="label">{{ $t("labels.active") }}</label>
        <doc-anchor :object="ctx.target" prop="momentumActive" />
      </div>
      <input-switch
        :value="ctx.target.momentumActive && momentum != null"
        @update:value="updateMomentum($event)"
      />
      <button
        v-if="ctx.target.momentumActive && momentum != null"
        type="button"
        class="button mt-2"
        @click="selectionStore.select(momentum)"
      >
        {{ $t("labels.editX", [$t("objectNames.momentum")]) }}
      </button>
    </drawer>

    <drawer
      v-if="features.stakeholder"
      :title="$t('objectNames.stakeholder', 2)"
      v-model:open="drawerStates.targetStakeholders"
    >
      <stakeholder-list item-type="target" :item-id="ctx.target.id" />
    </drawer>

    <!-- DESIGN -->
    <drawer
      v-if="features.design"
      :title="$t('labels.design')"
      v-model:open="drawerStates.targetDesign"
    >
      <element-design-menu :element-uid="uid" />
    </drawer>

    <!-- DELETE -->
    <menu-group class="mt-auto">
      <button
        type="button"
        class="button button_secondary mv-2"
        @click="ctx.delete()"
      >
        {{ $t("actions.deleteTarget", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { splitUid } from "@/helpers/utility/Id";

import Drawer from "@core/ui/DrawerBlock.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import DocAnchor from "@editor/ui/DocumentationAnchor.vue";

import InputText from "@core/input/InputText.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import InputTextEditor from "@core/input/TextEditor.vue";
import ElementDesignMenu from "../element/ElementDesignMenu.vue";

import FlowList from "@editor/items/flow/FlowList.vue";
import RelationList from "@editor/items/relation/RelationList.vue";
import IndicatorList from "@editor/items/indicator/IndicatorList.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";
import StakeholderList from "@editor/items/stakeholder/StakeholderList.vue";

import ValueMenu from "@editor/items/target/TargetValueMenu.vue";

import { useTarget } from "@editor/items/target/useTarget";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useMomentumStore } from "@editor/items/momentum/momentums.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useFeatures } from "@editor/store/features.store";
import type { PartialIdentity, Uid } from "@/helpers/utility/Id";
import type { MomentumItem } from "../momentum/MomentumItem";

interface Props {
  uid: Uid<"target">;
  nodeUid?: Uid<"sceneNode">;
  subSelection?: null | { type: string; id: number };
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (
    e: "select",
    item: PartialIdentity<"flow" | "relation"> | null
  ): void;
  (
    e: "deselect",
    item: PartialIdentity<"flow" | "relation"> | null
  ): void;
}>();

const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const { selectSub } = selectionStore;

// const titleField = ref<InstanceType<typeof InputText>>();
const descriptionField = ref<InstanceType<typeof InputTextEditor>>();

const features = useFeatures();

// =====================
// TARGET

const ctx = useTarget(() => splitUid(props.uid).id);

function selectTarget(targetId: number | null) {
  if (targetId === ctx.target.id) return;
  if (targetId == null) {
    selectionStore.clearAll();
    return;
  }

  const target = useTargetStore().items[targetId];
  if (target == null) {
    selectionStore.clearAll();
  } else {
    selectionStore.select(target);
  }
}

// =====================
// DRAWERS

const drawerStates = useDrawerStates();

// =====================
// RELATIONS

const selectedRelation = computed(() => {
  if (props.subSelection == null) return null;
  if (props.subSelection.type != "relation") return null;
  return relationStore.items[props.subSelection.id];
});

// =====================
// MOMENTUM

const momentum = computed((): MomentumItem | null => {
  return useMomentumStore().byTarget[ctx.target.id] ?? null;
});

async function updateMomentum(value: boolean) {
  // create a momentum for the target if there isn't one.
  if (value === true && momentum.value == null) {
    await useMomentumStore().create({
      elementType: "target",
      elementId: ctx.target.id
    });
  }
  ctx.updateKey("momentumActive", value);
}
</script>

<style>
.target-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
